// components/HistoricalViewChips.tsx
import React, { useMemo } from 'react';
import { Chip } from '@mui/material';
import type { HistoricalView, UtilizationBucket } from '@domain/graphql.types';
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Bar, Cell, Tooltip as ReTooltip, ComposedChart, Line } from 'recharts';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

type Props = {
  siteId: string | number;
  buckets: HistogramBucket[] | null | undefined;
  includeMinMax?: boolean;
};

export type HistogramBucket = {
  endDate: string;
  percentageUtilization?: number;
  price?: number;
  minPrice?: number;
  maxPrice?: number;
};

export function ToHistogramBuckets(buckets: UtilizationBucket[] | null | undefined): HistogramBucket[] {
  return (buckets ?? []).map((b) => ({
    endDate: b.endDate,
    percentageUtilization: b.percentageUtilization,
    price: b.price,
  }));
}

type ChipProps = {
  hv: HistoricalView | null | undefined;
};

const CHART_MARGIN = { top: 8, right: 8, left: 8, bottom: 22 };
const Y_AXIS_WIDTH = 40;

export default function HistoricalViewChips({ hv }: ChipProps) {
  const pricingModel = useMemo(() => hv?.pricingModel ?? '—', [hv]);

  const portTypesLabel = useMemo(() => summarizeList(hv?.portTypes) ?? '—', [hv?.portTypes]);
  const hardwareTypesLabel = useMemo(() => summarizeList(hv?.hardwareTypes) ?? '—', [hv?.hardwareTypes]);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'left' }}>
      <SmallChip label={`Port types: ${portTypesLabel}`} />
      <SmallChip label={`Hardware: ${hardwareTypesLabel}`} />
      <SmallChip label={`Pricing model: ${pricingModel || '—'}`} />
    </div>
  );
}

function SmallChip({ label }: { label: React.ReactNode }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        borderRadius: 0.5,
        fontWeight: 'medium',
        fontSize: 13,
        height: 20,
        '& .MuiChip-label': { px: '4px' },
      }}
    />
  );
}

function summarizeList(list: readonly string[] | undefined | null): string | null {
  const limit = 3;
  if (!list || list.length === 0) return null;
  const uniq = Array.from(new Set(list.filter(Boolean)));
  if (uniq.length <= limit) return uniq.join(', ');
  const shown = uniq.slice(0, limit).join(', ');
  const more = uniq.length - limit;
  return `${shown} +${more} more`;
}

export function HistoricalUtilizationHistogram({ siteId, buckets, includeMinMax }: Props) {
  // Build chart points
  const data = useMemo(() => {
    return (buckets ?? []).map((b: { percentageUtilization?: number; price?: number; endDate: string; minPrice?: number; maxPrice?: number }, i: number) => {
      const pct = clamp0to100(Math.round((b.percentageUtilization ?? 0) * 100));
      return {
        index: i,
        label: String(i + 1),
        utilization: pct,
        price: b.price?.toFixed(2),
        minPrice: b.minPrice?.toFixed(2),
        maxPrice: b.maxPrice?.toFixed(2),
        ratio: pct / 100,
        endDate: b.endDate,
      };
    });
  }, [buckets]);

  const ticks = useMemo(() => {
    return (buckets ?? [])
      .map((b: { percentageUtilization?: any; price?: any; endDate: any }, index: number) => {
        return index % 4 === 0 ? b.endDate : null;
      })
      .filter(Boolean);
  }, [buckets]);

  /** Derive month segments for the label row under the chart */
  const monthSegments = useMemo(() => {
    type Seg = { key: string; label: string; count: number; startIndex: number; endIndex: number };
    const segs: Seg[] = [];
    let currentKey = '';
    data.forEach((d, i) => {
      const dt = new Date(d.endDate);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (key !== currentKey) {
        currentKey = key;
        segs.push({
          key,
          label: dt.toLocaleString('en-US', { month: 'short' }),
          count: 1,
          startIndex: i,
          endIndex: i,
        });
      } else {
        segs[segs.length - 1].count += 1;
        segs[segs.length - 1].endIndex = i;
      }
    });
    return segs;
  }, [data]);

  if (!data.length) return null;

  const { palette } = useTheme();
  const { t } = useTranslation();

  const gridTemplateColumns = monthSegments.map((s) => `${s.count}fr`).join(' ');
  return (
    <div
      style={{
        marginBottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: 300,
        marginTop: 4,
      }}
    >
      <strong style={{ paddingLeft: '5%', fontSize: 14 }}>{t('siteLookup.historicalView.titleWithPrice')}</strong>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
          <XAxis dataKey="label" tick={false} ticks={ticks} axisLine={false} height={0} />
          <YAxis yAxisId={0} domain={[0, 100]} tickFormatter={(t) => `${t}%`} width={Y_AXIS_WIDTH} tickMargin={2} orientation="left" />
          <YAxis
            yAxisId={1}
            tickFormatter={(value) => `$${value.toFixed(2)}`}
            width="auto"
            orientation="right"
            domain={[
              (min: number) => {
                const rangePad = Math.min(min * 0.75, 1.0);
                return Math.max(0, min - rangePad);
              },
              (max: number) => {
                const rangePad = Math.min(max * 0.25, 1.0);
                return max + rangePad;
              },
            ]}
          />
          <ReTooltip
            contentStyle={{
              padding: '4px 6px',
              fontSize: '12px',
              lineHeight: 1.0,
              borderRadius: 0,
            }}
            itemStyle={{
              fontSize: '12px',
              padding: 0,
              margin: 0,
            }}
            labelStyle={{
              fontSize: '12px',
              marginBottom: 2,
            }}
            formatter={(value, _, props) => {
              switch (props.dataKey) {
                case 'price':
                  return [`$${value}`, 'Price'];
                case 'minPrice':
                  return [`$${value}`, 'Minimum Price'];
                case 'maxPrice':
                  return [`$${value}`, 'Maximum Price'];
                case 'utilization':
                  return [`${value}%`, 'Utilization'];
                default:
                  return [value, ''];
              }
            }}
            labelFormatter={(_, payload) => {
              const d = payload?.[0]?.payload;
              if (!d?.endDate) return '';
              const dt = new Date(d.endDate);
              return dt.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
            }}
          />
          <Bar yAxisId={0} dataKey="utilization" isAnimationActive={false} radius={[3, 3, 0, 0]}>
            {data.map((d) => {
              const hue = Math.round((1 - d.ratio) * 120);
              const fill = `hsl(${hue} 70% 45%)`;
              return <Cell key={`util-week-${siteId}-${d.index}`} fill={fill} />;
            })}
          </Bar>
          <Line yAxisId={1} type="linear" dataKey="price" stroke={palette.primary.main} strokeWidth={2} dot={false} />
          {includeMinMax && <Line yAxisId={1} type="linear" dataKey="minPrice" stroke="green" strokeWidth={2} dot={false} />}
          {includeMinMax && <Line yAxisId={1} type="linear" dataKey="maxPrice" stroke="green" strokeWidth={2} dot={false} />}
        </ComposedChart>
      </ResponsiveContainer>
      <div
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns,
          gap: 0,
          padding: `0 ${CHART_MARGIN.right + Y_AXIS_WIDTH}px 2px ${CHART_MARGIN.left + Y_AXIS_WIDTH}px`,
          marginTop: '-6px',
        }}
      >
        {monthSegments.map((m) => (
          <div key={m.key} style={{ textAlign: 'center', fontSize: 12, lineHeight: '16px' }}>
            {m.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------ utils ------------------------------ */

function clamp0to100(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}
